import React, { useState } from 'react';

import { Heading } from '~/components/core';
import Grid from '~/components/core/Atomic/Grid/Grid';
import { reportErrorInProductionOrThrow } from '~/Utils';

import { useSysconfig } from '../../SystemConfigurationScreen';

import { ExecutionDetailsDialog } from './components/ExecutionDetailsDialog';
import { WorkflowExecutionsFilters } from './components/WorkflowExecutionsFilters';
import { WorkflowExecutionsTable } from './components/WorkflowExecutionsTable';
import { useWorkflowExecutionsActions } from './hooks/useWorkflowExecutionsActions';
import { useWorkflowExecutionsFilters } from './hooks/useWorkflowExecutionsFilters';
import { useWorkflowExecutionsPagination } from './hooks/useWorkflowExecutionsPagination';

const DEFAULT_ROWS_PER_PAGE = 25;

export const WorkflowExecutionsTab: React.FC = () => {
  const { organization } = useSysconfig() as { organization: { id: number } };
  const { filterParams, updateFilter, clearFilters } = useWorkflowExecutionsFilters();

  // Sort state management - only allow sorting by submitted_datetime in frontend
  const [sortByColumn, setSortByColumn] = useState<{ id: string; order: 'asc' | 'desc' }>({
    id: 'submitted_datetime',
    order: 'desc',
  });

  const sortString = `${sortByColumn.order === 'asc' ? '+' : '-'}${sortByColumn.id}`;

  const {
    pagination,
    data: executions,
    loading: isLoadingExecutions,
    reloadExecutions,
    statusCounts,
  } = useWorkflowExecutionsPagination({
    organizationId: organization.id,
    initialRowsPerPage: DEFAULT_ROWS_PER_PAGE,
    sortByColumn: sortString,
    // Pass filter parameters to trigger API requests on filter changes
    workflowId: filterParams.workflowId || null,
    ruleId: filterParams.ruleId || null,
    status: filterParams.status.length > 0 ? filterParams.status : null,
    startDate: filterParams.startDate || null,
    endDate: filterParams.endDate || null,
  });

  const { handleViewDetails, selectedExecution, detailsDialogOpen, handleCloseDetailsDialog } =
    useWorkflowExecutionsActions();

  const handleSortChange = (columnId: string, direction: 'asc' | 'desc') => {
    // Only allow sorting by submitted_datetime in the frontend
    if (columnId === 'submitted_datetime') {
      setSortByColumn({ id: columnId, order: direction });
      pagination.setPage(0); // Reset to first page when sorting changes
    } else {
      // Report unexpected sort column attempts - this should not happen in normal operation
      // since only submitted_datetime column is marked as sortable in the table
      reportErrorInProductionOrThrow(
        `Unexpected sort column requested: ${columnId}. Only 'submitted_datetime' is supported.`
      );
    }
  };

  return (
    <>
      <Grid container className="p-20">
        <Grid item xs={12} className="pb-12">
          <Heading variant="h3">Workflow Executions</Heading>
        </Grid>

        <WorkflowExecutionsFilters
          filterParams={filterParams}
          onFilterChange={updateFilter}
          onClearFilters={clearFilters}
          organizationId={organization.id}
          statusCounts={statusCounts}
        />

        <WorkflowExecutionsTable
          executions={executions}
          loading={isLoadingExecutions}
          pagination={pagination}
          onViewDetails={handleViewDetails}
          onRefresh={reloadExecutions}
          onSortChange={handleSortChange}
        />
      </Grid>

      <ExecutionDetailsDialog
        execution={selectedExecution}
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
      />
    </>
  );
};
